"use client";

import React, { useState, useTransition } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { ForgotPasswordSchema } from "@gsa/db/schema";
import { forgotPassword } from "@gsa/auth/actions";
import { forgotPasswordSchema } from "@gsa/db/schema";
import { LoadingButton } from "@gsa/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@gsa/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@gsa/ui/components/ui/form";
import { AlertCircleIcon, CheckIcon, ChevronRight } from "@gsa/ui/icons";
import { Input } from "@gsa/ui/input";

export default function ForgotPasswordForm() {
  const [success, setSuccess] = useState<string | undefined>();

  const [pending, startTransaction] = useTransition();
  const [error, setError] = useState<string | undefined>();

  const form = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),

    defaultValues: {},
  });

  const onSubmit = async (input: ForgotPasswordSchema) => {
    startTransaction(async () => {
      const result = await forgotPassword(input);

      if (result.ok === false) {
        setError(result.message);
      } else {
        setError("");
        setSuccess("Check your email for a link to reset your password");
      }

      // if (result?.ok === false) {
      //   setError(result?.message ?? "Failed to sign up");
      // } else {
      //   setError("");
      //   router.push("/account/profile");
      // }
    });
  };

  return (
    <Form {...form}>
      {success && (
        <Card className="mx-auto w-full md:max-w-md">
          <CardHeader className="space-y-1 text-center">
            <CheckIcon className="mx-auto h-12 w-12 text-green-400" />
            <CardTitle className="text-2xl">Password Reset Link Sent</CardTitle>
            <CardDescription>{success}</CardDescription>
          </CardHeader>
        </Card>
      )}
      {!success && (
        <div className=" mx-auto w-full md:max-w-md lg:py-32">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative grid gap-8 pb-24"
          >
            <Card>
              <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-2xl">Forgot Password</CardTitle>
                <CardDescription>
                  Enter your email below and we'll send you a link to reset your
                  password
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 ">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email Address <span className="text-red-600">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          type="email"
                          autoComplete="email"
                          required
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <div
                    className="flex h-8 items-end space-x-1"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <AlertCircleIcon className="h-5 w-5 text-red-500" />
                    <p className="text-sm text-red-500">{error}</p>
                  </div>
                )}
              </CardContent>

              <CardFooter>
                <LoadingButton
                  loading={pending}
                  disabled={pending}
                  className="w-full justify-between"
                >
                  Send Password Reset Link <ChevronRight />
                </LoadingButton>
              </CardFooter>
            </Card>
          </form>
        </div>
      )}
    </Form>
  );
}
